import { Form, Field, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { Label, Hint } from "@progress/kendo-react-labels";
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { Window } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SetValueOnObject } from '../../utils/UnsafeTypescriptUtil';
import { Lab, blankLab, apiModelToLab } from '../../models/lab';
import { protectedResources } from '../../authConfig';
import { LoadPage } from '../../components/LoadPage';
import { AppUserContext, LabContext } from '../../components/PvsContextProvider';
import { inputLabel, requiredValidator, postalCodeValidator, requiredEmailValidator, nonUSStateValidator } from "../../components/Validators";
import LabInvite from '../labInvites/Lab_Invite';
import { USState } from '../../models/USState';
import { LabPhoneNumber } from '../../components/Elements/LabPhoneNumber';
import { StateSelector } from '../../components/Elements/StateSelector';
import { CommentListAndButton } from '../Comments/Comment_List';
import SubmittedKitList from '../submittedKit/SubmittedKit_List';
import OperatorList from '../operator/Operator_List';
import PipetList from '../pipet/Pipet_List';
import ReportList from '../reports/Report_List';
import { useNotificationAPI } from '../../components/NotificationProvider';
import { useFetchWithMsal } from '../../hooks/useFetchWithMsal';
import { getPageTitle } from '../../utils/errorUtil';
import { ObjectTypes } from '../../models/comments';
import AppUserLabList from '../appUserLab/AppUserLab_List';

interface ManageLabProps {
    setLab?: Function,
    isPopup?: boolean,
    labId?: number,
    hideTabs?: boolean,
    children?: React.ReactNode
}

const ManageLab = (props: ManageLabProps) => {
    const params = useParams();
    const context = useContext(LabContext);
    const appUserContext = useContext(AppUserContext);
    const { addNotification } = useNotificationAPI();

    const [showLabInviteWindow, setShowLabInviteWindow] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);

    const { execute, isLoading } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    let labId = 0;
    if (props.labId) {
        labId = props.labId;
    } else if (params.LabId !== undefined) {
        labId = Number.parseInt(params.LabId);
    }
    const [lab, setLab] = useState(
        SetValueOnObject(structuredClone(blankLab), "Id", labId) as Lab);
    const [country, setCountry] = useState("United States of America");

    const onChange_Lab = (propName: string, value: string) => {
        let updatedLab: Lab = SetValueOnObject(lab, propName, value) as Lab;
        setLab(updatedLab);
    }
    const onChangeEvent_Lab = (e: any, propName: string) => {
        onChange_Lab(propName, e.target.value);
    }

    const submitLab = (formRenderProps: FormRenderProps) => {
        if (!formRenderProps.valid) {
            return;
        }

        const apiEndpoint = protectedResources.apiLab.endpoint +
            (lab.Id > 0 ?
                "UpdateLab" :
                "CreateLab");

        execute(lab.Id > 0 ? 'PUT' : 'POST', apiEndpoint, {
            Id: lab.Id,
            SytelineAccountNumber: lab.SytelineAccountNumber,
            FacilityName: lab.FacilityName,
            AddressLine1: lab.AddressLine1,
            AddressLine2: lab.AddressLine2,
            City: lab.City,
            State: lab.State,
            Country: country,
            PostalCode: lab.PostalCode,
            LabPhoneNumber: lab.LabPhoneNumber,
            LabPhoneNumberExtension: lab.LabPhoneNumberExtension,
            SendNotificationsTo: lab.SendNotificationsTo,
            PrimaryContactName: lab.PrimaryContactName
        })
            .then(
                ((result: any) => {
                    if (result?.id) {
                        if (lab.Id === 0 && !props.isPopup) {
                            window.history.replaceState(null, '', '/lab-manage/' + result?.id);
                        }
                        addNotification('Lab ' + (lab.Id > 0 ? 'updated' : 'created') + ': ' + lab.FacilityName, getPageTitle(), 'success');

                        let labModel = apiModelToLab(result);
                        if (props.setLab) {
                            props.setLab(labModel);
                        }
                        context.refresh();
                        setLab(labModel);
                    }
                })
            );
    };

    const getLab = () => {
        if (lab.Id > 0) {
            execute("GET", protectedResources.apiLab.endpoint + "GetLab/?Id=" + lab.Id)
                .then(
                    ((result: any) => {
                        if (result) {
                            const labModel = apiModelToLab(result);
                            setLab(labModel);
                            setLabPhoneNumber(labModel.LabPhoneNumber);
                            setCountry(labModel.Country);
                        }
                    })
                );
        } else {
            setLab(structuredClone(blankLab));
        }
    }

    const setLabPhoneNumber = (_labPhoneNumber: string) => {
        SetValueOnObject(lab, "LabPhoneNumber", _labPhoneNumber) as Lab;
    }

    useEffect(() => {
        getLab();
    }, [execute]);

    //#region State DropDown Code
    const setStateName = (e: DropDownListChangeEvent) => {
        let stateObject = e.target.value as USState;
        SetValueOnObject(lab, "State", stateObject.abbreviation) as Lab;
    }
    const setCountryName = (e: DropDownListChangeEvent) => {
        setCountry(e.target.value);
    }
    //#endregion

    const editLabInfoLayout = () => {
        return <Form initialValues={lab} key={JSON.stringify(lab)} render={(formRenderProps) =>
            <FormElement className='s-k-flex-50 p-4 rounded-lg streck-secondary'>
                <fieldset className={'k-form-fieldset'}>

                    <div className="row h-50">
                        <legend className={'s-k-form-legend k-form-legend text-center'} style={{ fontSize: '24px' }}>
                            {lab.Id > 0 ? `Manage Lab` : 'Create a new Lab'}
                        </legend>
                    </div>

                    {lab.Id > 0 &&
                        <div className="row">
                            <div className="mb-3 col">
                                Facility Account Number: {String(lab.Id).padStart(5, '0')}
                            </div>
                            {!props.hideTabs && !appUserContext.isReadOnly && <div>
                                <Button onClick={() => setShowLabInviteWindow(true)}>Invite Users to Lab</Button>
                            </div>}
                        </div>}

                    <div className="row">
                        <div className="p-1 col">
                            <Field name={'FacilityName'} autoFocus={true} component={inputLabel} validator={requiredValidator} label={'Facility Name*'} required={true} onChange={(event) => onChangeEvent_Lab(event, "FacilityName")} />
                        </div>
                        {appUserContext.adminView &&
                            <div className="p-1 col">
                                <Field name={'SytelineAccountNumber'} component={inputLabel} label={'Syteline Account Number'} required={false} onChange={(event) => onChangeEvent_Lab(event, "SytelineAccountNumber")} />
                            </div>
                        }
                    </div>

                    <div className="row">
                        <div className="p-1 col">
                            <Field name={'AddressLine1'} component={inputLabel} validator={requiredValidator} label={'Address Line 1*'} required={true} onChange={(event) => onChangeEvent_Lab(event, "AddressLine1")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="p-1 col">
                            <Field name={'AddressLine2'} component={inputLabel} label={'Address Line 2'} required={false} onChange={(event) => onChangeEvent_Lab(event, "AddressLine2")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="p-1 col">
                            <Field name={'City'} component={inputLabel} validator={requiredValidator} label={'City*'} required={true} onChange={(event) => onChangeEvent_Lab(event, "City")} />
                        </div>
                        <div className="p-1 col">
                            <Field name={'PostalCode'} component={inputLabel} validator={country === "United States of America" ? postalCodeValidator : undefined} label={'Postal Code*'} required={true} onChange={(event) => onChangeEvent_Lab(event, "PostalCode")} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="p-1 col">
                            {country === "United States of America" ? <>
                                <Label>
                                    State*
                                </Label>
                                <StateSelector state={lab.State} stateChange={setStateName}></StateSelector>
                            </> :
                                <Field name={'State'} validator={nonUSStateValidator} component={inputLabel} label={'State*'} required={true} onChange={(event) => onChangeEvent_Lab(event, "State")} />
                            }
                        </div>
                        <div className="p-1 col">
                            <Label>
                                Country*
                            </Label>
                            <DropDownList name={'Country'} value={country} data={["United States of America", "Canada", "Mexico"]} required={true} onChange={setCountryName} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="p-1 col">
                            <label className="k-label">Lab Phone Number*</label>
                            <LabPhoneNumber setValue={setLabPhoneNumber} labPhoneNumber={lab.LabPhoneNumber}></LabPhoneNumber>
                        </div>
                        <div className="p-1 col">
                            <Field name={'LabPhoneNumberExtension'} component={inputLabel} label={'Phone Extension'} required={false} onChange={(event) => onChangeEvent_Lab(event, "LabPhoneNumberExtension")} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="p-1 col">
                            <Field name={"PrimaryContactName"} component={inputLabel} label={"Primary Contact Name*"} required={true} onChange={(event) => onChangeEvent_Lab(event, "PrimaryContactName")} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="p-1 col">
                            <Field name={"SendNotificationsTo"} validator={requiredEmailValidator} component={inputLabel} label={"Primary Contact Email*"} required={true} onChange={(event) => onChangeEvent_Lab(event, "SendNotificationsTo")} />
                            <Hint id={"email"} direction={"start"}>This is the email that will appear on the generated report.</Hint>
                        </div>
                    </div>
                </fieldset>
                <div className="row justify-content-center">
                    <div className="col">
                        <div className="k-form-buttons d-flex justify-content-center">
                            <Button
                                onClick={() => submitLab(formRenderProps)}
                                type="submit"
                                hidden={appUserContext.isReadOnly}
                                disabled={!appUserContext.isStreckAdmin && lab.Id <= 0}
                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
                                {lab.Id > 0 ? "Update" : "Create"} Lab
                            </Button>
                        </div>
                    </div>
                </div>
            </FormElement>} />
    }

    return <LoadPage isLoading={isLoading}>
        {showLabInviteWindow &&
            <Window title={'Invite Users to Lab'} onClose={() => { setShowLabInviteWindow(false); }} initialHeight={375} initialWidth={500}>
                <LabInvite labId={lab.Id} facilityName={lab.FacilityName}></LabInvite>
            </Window>}
        {lab.Id === 0 || props.hideTabs ?
            <span style={{ display: "inline-block" }}>
                {editLabInfoLayout()}
            </span> :
            <span>
                <h2>{lab.FacilityName}</h2>
                <TabStrip selected={selectedTab} onSelect={(e) => setSelectedTab(e.selected)}>
                    <TabStripTab title="Lab Info">
                        {editLabInfoLayout()}
                    </TabStripTab>
                    <TabStripTab title="Submitted Kits">
                        <SubmittedKitList labId={lab.Id}></SubmittedKitList>
                    </TabStripTab>
                    <TabStripTab title="Operators">
                        <OperatorList labId={lab.Id}></OperatorList>
                    </TabStripTab>
                    <TabStripTab title="Pipets">
                        <PipetList labId={lab.Id}></PipetList>
                    </TabStripTab>
                    <TabStripTab title="Reports">
                        <ReportList
                            labId={lab.Id}>
                        </ReportList>
                    </TabStripTab>
                    {appUserContext.adminView &&
                        <TabStripTab title="Comments">
                            <CommentListAndButton
                                objectId={lab.Id}
                                objectType={ObjectTypes.Lab}
                                isReadOnly={appUserContext.isReadOnly}>
                            </CommentListAndButton>
                        </TabStripTab>}
                    <TabStripTab title="Lab Members">
                        <AppUserLabList
                            labId={lab.Id}>
                        </AppUserLabList>
                    </TabStripTab>
                </TabStrip>
            </span>}
    </LoadPage>
}

export default ManageLab;